import React, { memo, useCallback, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import isNil from 'lodash/isNil';
import { lazyForPaint } from 'react-loosely-lazy';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useFieldValue } from '@atlassian/jira-issue-field-base';
import type IssueOriginalEstimateFieldType from '@atlassian/jira-issue-field-original-estimate';
import {
	AsyncOriginalEstimateInlineEdit,
	OriginalEstimateInlineEditErrorBoundary,
} from '@atlassian/jira-issue-field-original-estimate-inline-edit';
import { OriginalEstimateReadView } from '@atlassian/jira-issue-field-original-estimate-inline-edit-readview';
import { IssueOriginalEstimateFieldBoundary } from '@atlassian/jira-issue-field-original-estimate/async';
import { TimeEstimateKey } from '@atlassian/jira-providers-issue';
import { useShouldRefetchEpicPanel } from '@atlassian/jira-software-backlog-panel/src/services/epic-panel-refetch';
import {
	timeTrackingFormatter,
	type TimeTrackingOptions,
} from '@atlassian/jira-time-tracking-formatter';
import { timeTrackingFormatterLongForm } from '@atlassian/jira-time-tracking-formatter/src/main';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics';
import {
	isNewInlineEditEstimateComponentsEnabled,
	isBacklogCardEstimatesA11yImprovementsEnabled,
} from '../../../../../feature-flags';
import { ORIGINAL_ESTIMATE } from '../../../../../model/constants';
import type { EstimationType } from '../../../../../model/types';
import {
	cancelInlineEditOriginalEstimateInteractionMark,
	setInlineEditOriginalEstimateInteractionStartMark,
	setInlineEditOriginalEstimateInteractionStopMark,
} from '../../../../../services/performance/interaction-metric-inline-edit';
import { useBacklogDispatch, useBacklogSelector } from '../../../../../state';
import { refetchVersions } from '../../../../../state/actions/version';
import { getCards } from '../../../../../state/selectors/cards';
import { useIsCMPBacklog } from '../../../../../state/state-hooks/capabilities';
import SLAExperienceTracker from '../../../../sla-experience-tracker';
import { CARD_CONTAINER_SELECTOR } from '../../constants';
import { CardContainerControl } from '../../styled';
import EstimateFieldWrapper from '../estimate-field-wrapper';
import messages from './messages';
import OriginalEstimateFieldStatic from './original-estimate-field-static';
import { timeTrackingConfigTransformer } from './utils';

const IssueOriginalEstimateField = lazyForPaint<typeof IssueOriginalEstimateFieldType>(
	() =>
		import(
			/* webpackChunkName: "async-issue-field-original-estimate" */ '@atlassian/jira-issue-field-original-estimate'
		),
);

export type Props = {
	issueId: number;
	issueKey: string;
	estimationType: EstimationType;
	timeTrackingOptions: TimeTrackingOptions;
	isUserAtLeastMember: boolean;
	shouldRenderRichCard: boolean;
	shouldRenderEstimatesColumn: boolean;
};

const OriginalEstimateField = ({
	issueId,
	issueKey,
	estimationType,
	timeTrackingOptions,
	isUserAtLeastMember,
	shouldRenderRichCard,
	shouldRenderEstimatesColumn,
}: Props) => {
	const isCompanyManaged = useIsCMPBacklog();
	const hasEstimate = useBacklogSelector((state) => !!getCards(state)[String(issueId)]?.estimate);
	const dispatch = useBacklogDispatch();

	const [_, { triggerEpicPanelRefetch }] = useShouldRefetchEpicPanel();

	const intl = useIntl();
	const [originalEstimateFieldValue] = useFieldValue({
		issueKey,
		fieldKey: TimeEstimateKey,
	});

	const handleConfirm = useCallback(() => {
		setInlineEditOriginalEstimateInteractionStartMark();
	}, []);

	const handleUpdate = useCallback(() => {
		setInlineEditOriginalEstimateInteractionStopMark(isCompanyManaged);
		dispatch(refetchVersions());
		if (isCompanyManaged) {
			triggerEpicPanelRefetch();
		}
	}, [dispatch, isCompanyManaged, triggerEpicPanelRefetch]);

	const handleFailure = useCallback(() => {
		cancelInlineEditOriginalEstimateInteractionMark();
	}, []);

	if (!shouldRenderEstimatesColumn && (estimationType !== ORIGINAL_ESTIMATE || !hasEstimate)) {
		return null;
	}

	const isOriginalEstimateAccessible = !isNil(originalEstimateFieldValue);
	const value = isOriginalEstimateAccessible
		? timeTrackingFormatter(originalEstimateFieldValue, timeTrackingOptions, intl)
		: undefined;

	const placeholder = '2w 4d 6h 45m';
	const invalidTimeFormatMessage = (
		<FormattedMessage
			{...messages.invalidTimeFormatMessage}
			values={{ format: <b>{placeholder}</b> }}
		/>
	);

	let ariaLabel = '';
	if (ff('sea-2524-backlog-card-estimates-a11y-improvements_1ljqi')) {
		const longFormValue =
			isOriginalEstimateAccessible && originalEstimateFieldValue > 0
				? timeTrackingFormatterLongForm(originalEstimateFieldValue, timeTrackingOptions, intl)
				: undefined;

		ariaLabel = longFormValue
			? `${intl.formatMessage(messages.originalEstimateAriaLabel)}: ${longFormValue}`
			: `${intl.formatMessage(messages.originalEstimateAriaLabel)}: ${intl.formatMessage(messages.originalEstimateAriaLabelNullValue)}`;
	}

	const OriginalEstimateFieldBoundary = isNewInlineEditEstimateComponentsEnabled()
		? OriginalEstimateInlineEditErrorBoundary
		: IssueOriginalEstimateFieldBoundary;

	const AsyncOriginalEstimateField = isNewInlineEditEstimateComponentsEnabled()
		? AsyncOriginalEstimateInlineEdit
		: IssueOriginalEstimateField;

	let issueOriginalEstimateFieldStatic: JSX.Element;
	if (isNewInlineEditEstimateComponentsEnabled()) {
		// Both flags on
		if (ff('sea-2524-backlog-card-estimates-a11y-improvements_1ljqi')) {
			issueOriginalEstimateFieldStatic = (
				<EstimateFieldWrapper
					tooltipLabel={intl.formatMessage(messages.originalEstimateTooltip)}
					ariaLabel={ariaLabel}
				>
					<OriginalEstimateReadView isEditable={false} value={value || '0m'} />
				</EstimateFieldWrapper>
			);
		}
		// New inline estimate flag on, a11y flag off
		else {
			issueOriginalEstimateFieldStatic = (
				<OriginalEstimateReadView isEditable={false} value={value || '0m'} />
			);
		}
	}
	// A11y flag on, new inline estimate flag off
	else if (ff('sea-2524-backlog-card-estimates-a11y-improvements_1ljqi')) {
		issueOriginalEstimateFieldStatic = (
			<OriginalEstimateFieldStatic
				value={value}
				valueInSeconds={originalEstimateFieldValue}
				hasEstimate={hasEstimate}
				timeTrackingOptions={timeTrackingOptions}
			/>
		);
	}
	// Neither flag on
	else {
		issueOriginalEstimateFieldStatic = (
			<OriginalEstimateFieldStatic value={value} hasEstimate={hasEstimate} />
		);
	}

	if (isUserAtLeastMember && shouldRenderRichCard) {
		if (ff('sea-2524-backlog-card-estimates-a11y-improvements_1ljqi')) {
			return (
				<OriginalEstimateFieldBoundary
					packageName={BACKLOG_PACKAGE_NAME}
					fallback={issueOriginalEstimateFieldStatic}
				>
					<SLAExperienceTracker
						experienceTarget="inline original estimate"
						experienceTargetId={issueKey}
						additionalAttributes={{ issueKey }}
					>
						<OriginalEstimateFieldWrapper shouldRenderEstimatesColumn={shouldRenderEstimatesColumn}>
							{hasEstimate && (
								<OriginalEstimateFieldContainer hasValue={isOriginalEstimateAccessible}>
									<EstimateFieldWrapper
										tooltipLabel={intl.formatMessage(messages.originalEstimateTooltip)}
										ariaLabel={ariaLabel}
									>
										<AsyncOriginalEstimateField
											isEditable
											isMobile={false}
											isFitContainerWidthReadView
											fieldId={TimeEstimateKey}
											issueId={String(issueId)}
											issueKey={issueKey}
											config={timeTrackingConfigTransformer(timeTrackingOptions)}
											placeholder={placeholder}
											timeTrackingValue={{}} // NOTE: time tracking is irrelevant in Backlog inline original estimate
											invalidTimeFormatMessage={invalidTimeFormatMessage}
											isBacklogComponent
											onUpdate={handleUpdate}
											onConfirm={handleConfirm}
											onFailure={handleFailure}
										/>
									</EstimateFieldWrapper>
								</OriginalEstimateFieldContainer>
							)}
						</OriginalEstimateFieldWrapper>
					</SLAExperienceTracker>
				</OriginalEstimateFieldBoundary>
			);
		}

		return (
			<OriginalEstimateFieldBoundary
				packageName={BACKLOG_PACKAGE_NAME}
				fallback={issueOriginalEstimateFieldStatic}
			>
				<SLAExperienceTracker
					experienceTarget="inline original estimate"
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<OriginalEstimateFieldWrapper shouldRenderEstimatesColumn={shouldRenderEstimatesColumn}>
						{hasEstimate && (
							<OriginalEstimateFieldContainer hasValue={isOriginalEstimateAccessible}>
								<AsyncOriginalEstimateField
									isEditable
									isMobile={false}
									isFitContainerWidthReadView
									fieldId={TimeEstimateKey}
									issueId={String(issueId)}
									issueKey={issueKey}
									config={timeTrackingConfigTransformer(timeTrackingOptions)}
									placeholder={placeholder}
									timeTrackingValue={{}} // NOTE: time tracking is irrelevant in Backlog inline original estimate
									invalidTimeFormatMessage={invalidTimeFormatMessage}
									isBacklogComponent
									onUpdate={handleUpdate}
									onConfirm={handleConfirm}
									onFailure={handleFailure}
								/>
							</OriginalEstimateFieldContainer>
						)}
					</OriginalEstimateFieldWrapper>
				</SLAExperienceTracker>
			</OriginalEstimateFieldBoundary>
		);
	}

	return issueOriginalEstimateFieldStatic;
};

export default memo<Props>(OriginalEstimateField);

type OriginalEstimateFieldProps = {
	hasValue: boolean;
	children: ReactNode;
};

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OriginalEstimateFieldContainerControl = styled.div<OriginalEstimateFieldProps>`
	cursor: default; /* NOTE: cursor stylings for hover behavior should be handled inside the field component, not here */

	${() =>
		isBacklogCardEstimatesA11yImprovementsEnabled() &&
		`
        display: flex;
        height: 100%;
        align-items: center;
        `}

	min-width: ${gridSize * 10}px;
	overflow: inherit;

	[data-testid^='issue-field-original-estimate.ui.view.badge'] {
		display: block;
		max-width: ${gridSize * 10}px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		& > span {
			display: block;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
	}

	/* build our own focus effect using box-shadow because AK's focus takes more space and doesn't respond to active state */
	&:focus,
	&:focus-within {
		outline: none;
	}

	/** Prevent double border on focus by overriding the number field component, without impacting it anywhere else */
	button[aria-label='Edit']:focus + div {
		${() =>
			isBacklogCardEstimatesA11yImprovementsEnabled() &&
			isNewInlineEditEstimateComponentsEnabled() &&
			'border-color: transparent;'}
	}

	/* undo hide if card is focused or hovered */
	${CardContainerControl}:hover &,
    ${CardContainerControl}:active &,
    ${CardContainerControl}:focus &,
    ${CardContainerControl}:focus-within & {
		position: inherit;
		opacity: inherit;
		pointer-events: inherit;
	}

	/* NOTE: The AK component used by AsyncIssueOriginalEstimateField is from an older version
    * The following styling adjustments may be subject to change/remove after AK components are bumped in BENTO
    */

	* {
		display: flex; /* AK badge has several inline-block elements which result in misalignment, so we override display: inline-block to display: flex */

		/* NOTE: This is used to remove the focus blue ring around an invisible element inside AK component (most likely a screen reader label)
            * when the story point component is focussed and hovered at the same time
            */
		&:empty {
			box-shadow: none;
		}
	}

	& > div > div {
		margin-left: 0; /* remove a margin-left: -8px offset from AK badge */

		&[data-component-selector='jira-issue-field-inline-edit-readonly-view-container'] {
			/*
             * NonEditableMargin overrides.
             * The top margin here negates the negative margin being applied to the parent (where it's used to create extra space for the input element).
             * This component's original margin is intended for use within bento. So, we set all the other values to 0 too.
             */
			margin: ${() =>
				ff('jsw.backlog.improve-styling-issues-with-browser-font-size-settings')
					? `${token('space.100', '8px')} 0 0 0`
					: undefined};
		}

		& > div > div {
			display: flex; /* AK badge has a higher precedence display: inline-block on this level, so we override it specifically here */
		}
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const OriginalEstimateFieldContainerExperiment = styled2.div<OriginalEstimateFieldProps>(
	{
		/* NOTE: cursor stylings for hover behavior should be handled inside the field component, not here */
		cursor: 'default',

		/* build our own focus effect using box-shadow because AK's focus takes more space and doesn't respond to active state */
		'&:focus, &:focus-within': {
			outline: 'none',
		},

		/* Prevent double border on focus by overriding the number field component, without impacting it anywhere else */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"button[aria-label='Edit']:focus + div": {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			borderColor: () =>
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				isBacklogCardEstimatesA11yImprovementsEnabled() &&
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				isNewInlineEditEstimateComponentsEnabled() &&
				'transparent',
		},

		/*
		 * NOTE: The AK component used by AsyncIssueOriginalEstimateField is
		 * from an older version. The following styling adjustments may be
		 * subject to change/remove after AK components are bumped in BENTO.
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		div: {
			/*
			 * AK badge has several inline-block elements which result in
			 * misalignment, so we override display: inline-block to display: flex
			 */
			display: 'flex',
			/*
			 * NOTE: This is used to remove the focus blue ring around an
			 * invisible element inside AK component (most likely a screen
			 * reader label) when the story point component is focussed and
			 * hovered at the same time
			 */
			'&:empty': {
				boxShadow: 'none',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > div > div': {
			/* remove a margin-left: -8px offset from AK badge */
			marginLeft: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"&[data-component-selector='jira-issue-field-inline-edit-readonly-view-container']": {
				/*
				 * NonEditableMargin overrides.
				 * The top margin here negates the negative margin being applied to the parent (where it's used to create extra space for the input element).
				 * This component's original margin is intended for use within bento. So, we set all the other values to 0 too.
				 */
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				margin: () =>
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					ff('jsw.backlog.improve-styling-issues-with-browser-font-size-settings')
						? `${token('space.100', '8px')} 0 0 0`
						: undefined,
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& > div > div': {
				/* AK badge has a higher precedence display: inline-block on this level, so we override it specifically here */
				display: 'flex',
			},
		},

		/* undo hide if card is focused or hovered */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		[`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:hover &, ` +
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:active &, ` +
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus &, ` +
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus-within &`]: {
			position: 'inherit',
			opacity: 'inherit',
			pointerEvents: 'inherit',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		minWidth: `${gridSize * 10}px`,
		overflow: 'inherit',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"[data-testid^='issue-field-original-estimate.ui.view.badge']": {
			display: 'block',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			maxWidth: `${gridSize * 10}px`,
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > span': {
				display: 'block',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				textOverflow: 'ellipsis',
			},
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	() => ({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		...(isBacklogCardEstimatesA11yImprovementsEnabled() && {
			display: 'flex',
			height: '100%',
			alignItems: 'center',
		}),
	}),
);

const OriginalEstimateFieldContainer = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	OriginalEstimateFieldContainerExperiment,
	OriginalEstimateFieldContainerControl,
);

// empty column for the cards that without original estimates
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OriginalEstimateFieldWrapper = styled2.div<{
	shouldRenderEstimatesColumn: boolean;
}>(
	{},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.shouldRenderEstimatesColumn && {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			minWidth: `${gridSize * 10}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			maxWidth: `${gridSize * 10}px`,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	() =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		isBacklogCardEstimatesA11yImprovementsEnabled() && {
			display: 'flex',
			alignSelf: 'stretch',
			alignItems: 'center',
		},
);
